
import React, { useState, useEffect } from 'react';
import { Bell, Users, Mail, Clock, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';

const Step8Notification = ({ wizardData, updateWizardData }) => {
  const [recipients, setRecipients] = useState([]);

  useEffect(() => {
    // Generate mock recipients from selected students
    const recipientsList = wizardData.selectedStudents?.map((student) => ({
      id: student.id,
      studentName: student.name,
      parentName: `Parent of ${student.name}`,
      email: `parent${student.id}@example.com`,
      phone: `+1 (555) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
      busNumber: student.busNumber,
    })) || [];
    
    setRecipients(recipientsList);

    // Generate default message
    if (!wizardData.notification?.message) {
      const defaultMessage = generateDefaultMessage();
      updateWizardData('notification', {
        ...wizardData.notification,
        message: defaultMessage,
        recipients: recipientsList,
        timing: 'immediate',
      });
    }
  }, [wizardData.selectedStudents]);

  const generateDefaultMessage = () => {
    const mergeType = wizardData.mergeType === 'instant' ? 'immediate' : 'scheduled';
    const dateInfo = wizardData.mergeDate
      ? ` on ${format(wizardData.mergeDate, 'MMMM d, yyyy')}`
      : '';
    const shiftInfo = wizardData.shift ? ` for the ${wizardData.shift.name}` : '';

    return `Dear Parents/Guardians,

We are writing to inform you about an ${mergeType} bus route merge${dateInfo}${shiftInfo}.

Your child's bus has been merged with ${wizardData.destinationBus?.busNumber}. This change is being made to ensure safe and efficient transportation.

Key Details:
• New Bus: ${wizardData.destinationBus?.busNumber}
• Driver: ${wizardData.useDefaultDriver ? wizardData.destinationBus?.driver : wizardData.driver?.name}

Your child's pickup/drop-off location and timing remain the same. If you have any questions or concerns, please contact the school administration.

Thank you for your understanding and cooperation.

Best regards,
School Administration`;
  };

  const handleMessageChange = (value) => {
    updateWizardData('notification', {
      ...wizardData.notification,
      message: value,
    });
  };

  const handleTimingChange = (value) => {
    updateWizardData('notification', {
      ...wizardData.notification,
      timing: value,
    });
  };

  const timingOptions = [
    {
      id: 'immediate',
      label: 'Send Immediately',
      description: 'Send notifications right after merge activation',
    },
    {
      id: '1hour',
      label: '1 Hour Before',
      description: 'Send notifications 1 hour before the scheduled merge',
      disabled: wizardData.mergeType === 'instant',
    },
    {
      id: '24hours',
      label: '24 Hours Before',
      description: 'Send notifications 1 day before the scheduled merge',
      disabled: wizardData.mergeType === 'instant',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600 mb-2">
          Customize the notification message and timing. All affected parents and guardians will
          receive this notification via email and SMS.
        </p>
      </div>

      {/* Notification Timing */}
      <Card className="p-6">
        <Label htmlFor="timing" className="text-base font-semibold mb-3 block">
          Notification Timing
        </Label>
        <Select
          value={wizardData.notification?.timing || 'immediate'}
          onValueChange={handleTimingChange}
        >
          <SelectTrigger id="timing" className="w-full h-12 text-base">
            <SelectValue placeholder="Choose timing" />
          </SelectTrigger>
          <SelectContent>
            {timingOptions.map((option) => (
              <SelectItem
                key={option.id}
                value={option.id}
                disabled={option.disabled}
                className="text-base"
              >
                <div className="flex items-center gap-2 py-1">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.description}</div>
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      {/* Message Template */}
      <Card className="p-6">
        <Label htmlFor="message" className="text-base font-semibold mb-3 block">
          Notification Message
        </Label>
        <Textarea
          id="message"
          value={wizardData.notification?.message || ''}
          onChange={(e) => handleMessageChange(e.target.value)}
          className="min-h-[300px] font-mono text-sm"
          placeholder="Enter notification message..."
        />
        <p className="text-xs text-gray-500 mt-2">
          This message will be sent to all parents and guardians of affected students.
        </p>
      </Card>

      {/* Recipients List */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-gray-600" />
          <h3 className="text-base font-semibold text-gray-900">
            Recipients ({recipients.length})
          </h3>
        </div>
        
        <div className="max-h-[300px] overflow-y-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Student</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Parent/Guardian</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Contact</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">Original Bus</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {recipients.slice(0, 10).map((recipient) => (
                <tr key={recipient.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-900">{recipient.studentName}</td>
                  <td className="px-4 py-3 text-gray-700">{recipient.parentName}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <div className="flex flex-col gap-1">
                      <span className="flex items-center gap-1 text-xs">
                        <Mail className="h-3 w-3" />
                        {recipient.email}
                      </span>
                      <span className="text-xs">{recipient.phone}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {recipient.busNumber}
                    </span>
                  </td>
                </tr>
              ))}
              {recipients.length > 10 && (
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-center text-gray-500 text-sm">
                    ... and {recipients.length - 10} more recipients
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Preview Card */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Bell className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Notification Preview</h4>
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                <strong>Recipients:</strong> {recipients.length} parents/guardians
              </p>
              <p>
                <strong>Delivery Method:</strong> Email & SMS
              </p>
              <p>
                <strong>Timing:</strong>{' '}
                {timingOptions.find((opt) => opt.id === wizardData.notification?.timing)?.label || 'Immediate'}
              </p>
              {wizardData.mergeType === 'scheduled' && wizardData.mergeDate && (
                <p>
                  <strong>Scheduled For:</strong>{' '}
                  {format(wizardData.mergeDate, 'MMMM d, yyyy')} at{' '}
                  {wizardData.shift?.name || 'TBD'}
                </p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Warning for Instant Merge */}
      {wizardData.mergeType === 'instant' && (
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Since this is an instant merge, notifications will be sent
                immediately after activation. Make sure all information is correct before proceeding.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Step8Notification;
